import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlay, 
  FiSearch, 
  FiRefreshCw,
  FiEye,
  FiClock,
  FiAlertCircle,
  FiLock
} from 'react-icons/fi';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { podcastsAPI, Podcast, subscriptionAPI, authAPI } from '../services/api';
import { canUsePodcast } from '../utils/materialAccess';
import MaterialPagination from './MaterialPagination';
import PodcastVideoThumbnail from './PodcastVideoThumbnail';
import './MaterialPagination.css';
import './PodcastPage.css';
import './Dashboard.css';

interface PodcastPageProps {
  onLogout?: () => void;
  userEmail?: string;
}

const PodcastPage: React.FC<PodcastPageProps> = ({ onLogout, userEmail }) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [hoveredPodcastId, setHoveredPodcastId] = useState<number | null>(null);
  const previewVideoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  const hoverTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout | null }>({});
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
    is_staff: false
  });

  useEffect(() => {
    fetchUserProfile();
    checkSubscription();
  }, [userEmail]);

  useEffect(() => {
    if (hasSubscription !== null) {
      fetchPodcasts();
    }
  }, [hasSubscription, userInfo.is_staff, page, searchTerm]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_staff: profile.is_staff || false
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getSubscriptionStatus();
      const profile = await authAPI.getProfile();
      const isStaff = profile.is_staff || false;
      setHasSubscription(status.has_subscription || isStaff);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasSubscription(false);
    }
  };

  const fetchPodcasts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await podcastsAPI.listPodcasts({
        page,
        page_size: 24,
        q: searchTerm.trim() || undefined,
      });
      setPodcasts(res.results);
      setTotalPages(res.total_pages);
      setTotalCount(res.count);
    } catch (err: any) {
      console.error('Error fetching podcasts:', err);
      if (err.message && err.message.includes('subscription')) {
        setError('Active subscription required to access podcasts. Please contact administrator.');
      } else {
        setError(err.message || 'Failed to load podcasts');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPodcast = (podcast: Podcast) => {
    if (!canUsePodcast(hasSubscription, userInfo.is_staff, podcast.is_free_public)) {
      window.alert(
        'This podcast requires an active subscription. Choose one marked Free, or contact your administrator.'
      );
      return;
    }
    navigate(`/material/podcast/${podcast.id}`);
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePodcastMouseEnter = (podcast: Podcast) => {
    if (!canUsePodcast(hasSubscription, userInfo.is_staff, podcast.is_free_public)) {
      return;
    }
    if (hoverTimeoutRef.current[podcast.id]) {
      clearTimeout(hoverTimeoutRef.current[podcast.id]!);
    }
    hoverTimeoutRef.current[podcast.id] = setTimeout(() => {
      setHoveredPodcastId(podcast.id);
    }, 500);
  };

  const handlePodcastMouseLeave = (podcastId: number) => {
    // Clear timeout if mouse leaves before preview shows
    if (hoverTimeoutRef.current[podcastId]) {
      clearTimeout(hoverTimeoutRef.current[podcastId]!);
      hoverTimeoutRef.current[podcastId] = null;
    }
    
    // Stop video and hide preview
    const video = previewVideoRefs.current[podcastId];
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    
    setHoveredPodcastId(null);
  };

  const handlePreviewVideoRef = (podcastId: number, video: HTMLVideoElement | null) => {
    previewVideoRefs.current[podcastId] = video;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(hoverTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
      Object.values(previewVideoRefs.current).forEach(video => {
        if (video) {
          video.pause();
          video.src = '';
        }
      });
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="podcast-page">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div>
                <h1 className="page-title">Podcasts</h1>
                <p className="page-description">Watch educational videos and improve your skills</p>
              </div>
            </motion.div>

            {hasSubscription === false && !userInfo.is_staff && (
              <motion.div
                className="subscription-info-banner podcast-info-banner"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 20 })}
                <div>
                  <h3>Limited library</h3>
                  <p>
                    Podcasts marked <strong>Free</strong> are available without a subscription. Subscribe for the
                    full catalog.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
                <button onClick={fetchPodcasts} className="retry-btn">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span> Retry</span>
                </button>
              </motion.div>
            )}

            {/* Search Bar */}
            <motion.div
              className="search-container"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="search-box">
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                <input
                  type="text"
                  placeholder="Search podcasts by name or description..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1);
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>
            </motion.div>

            {/* Loading State */}
            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                <p>Loading podcasts...</p>
              </div>
            ) : podcasts.length === 0 ? (
              <div className="empty-state">
                {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 48 })}
                <p>No podcasts found</p>
                {searchTerm && (
                  <p className="empty-state-hint">Try a different search term</p>
                )}
              </div>
            ) : (
              <>
              <motion.div
                className="podcasts-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {podcasts.map((podcast) => {
                  const unlocked = canUsePodcast(hasSubscription, userInfo.is_staff, podcast.is_free_public);
                  return (
                  <motion.div
                    key={podcast.id}
                    className={`podcast-card ${!unlocked ? 'podcast-card-locked' : ''}`}
                    variants={itemVariants}
                    whileHover={unlocked ? { y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.3)' } : {}}
                    onClick={() => handleSelectPodcast(podcast)}
                    onMouseEnter={() => handlePodcastMouseEnter(podcast)}
                    onMouseLeave={() => handlePodcastMouseLeave(podcast.id)}
                  >
                    <div className={`podcast-thumbnail ${hoveredPodcastId === podcast.id ? 'preview-active' : ''}`}>
                      {hoveredPodcastId === podcast.id ? (
                        <video
                          ref={(el) => handlePreviewVideoRef(podcast.id, el)}
                          className="podcast-preview-video"
                          src={podcastsAPI.getStreamURL(podcast.id)}
                          muted
                          loop={false}
                          playsInline
                          preload="metadata"
                          onLoadedMetadata={(e) => {
                            const video = e.currentTarget;
                            // Set video to start at 25% or 10 seconds, whichever is less
                            const startTime = Math.min(10, (podcast.duration || 0) / 4);
                            video.currentTime = startTime;
                            // Auto-play after a short delay
                            setTimeout(() => {
                              if (hoveredPodcastId === podcast.id) {
                                video.play().catch(err => {
                                  console.error('Error auto-playing preview:', err);
                                });
                              }
                            }, 200);
                          }}
                          onEnded={(e) => {
                            // Loop the preview by restarting from the beginning
                            const video = e.currentTarget;
                            const startTime = Math.min(10, (podcast.duration || 0) / 4);
                            video.currentTime = startTime;
                            if (hoveredPodcastId === podcast.id) {
                              video.play().catch(err => {
                                console.error('Error looping preview:', err);
                              });
                            }
                          }}
                        />
                      ) : (
                        <>
                          <PodcastVideoThumbnail
                            podcastId={podcast.id}
                            alt={podcast.name}
                            canLoad={unlocked}
                          />
                          <div className="podcast-overlay">
                            <div className="podcast-play-icon">
                              {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 48 })}
                            </div>
                          </div>
                        </>
                      )}
                      {podcast.duration && (
                        <div className="podcast-duration-badge">
                          {formatDuration(podcast.duration)}
                        </div>
                      )}
                      {/* Watch Progress Bar */}
                      {(() => {
                        const hasProgress = podcast.watch_progress && podcast.watch_progress.progress_percentage > 0;
                        const progressSeconds = podcast.watch_progress?.progress_seconds || 0;
                        const progressPercentage = podcast.watch_progress?.progress_percentage || 0;
                        
                        console.log(`[PodcastPage] Rendering podcast ${podcast.id}:`, {
                          hasProgress,
                          watch_progress: podcast.watch_progress,
                          progress_seconds: progressSeconds,
                          progress_percentage: progressPercentage,
                          podcast_duration: podcast.duration,
                          willShowBar: hasProgress && podcast.watch_progress
                        });
                        
                        // Show progress bar if we have progress data
                        // Calculate percentage manually if duration is available
                        if (podcast.watch_progress && progressSeconds > 0) {
                          let percentage = progressPercentage;
                          
                          // If percentage is 0 but we have duration, calculate it
                          if (percentage === 0 && podcast.duration && podcast.duration > 0) {
                            percentage = (progressSeconds / podcast.duration) * 100;
                            console.log(`[PodcastPage] Calculated percentage manually: ${percentage}% (${progressSeconds}s / ${podcast.duration}s)`);
                          }
                          
                          // Show progress bar if percentage > 0
                          if (percentage > 0) {
                            return (
                              <div className="podcast-progress-bar">
                                <div 
                                  className="podcast-progress-filled"
                                  style={{ width: `${Math.min(100, percentage)}%` }}
                                />
                              </div>
                            );
                          } else {
                            console.log(`[PodcastPage] Cannot show progress bar: percentage=${percentage}, duration=${podcast.duration}, progress_seconds=${progressSeconds}`);
                          }
                        }
                        return null;
                      })()}
                    </div>
                    <div className="podcast-info">
                      <h3 className="podcast-name">
                        <span className="podcast-name-text">{podcast.name}</span>
                        {podcast.is_free_public && (
                          <span className="podcast-free-badge">Free</span>
                        )}
                        {!unlocked && (
                          <span className="podcast-lock-badge" title="Subscription required">
                            {React.createElement(FiLock as React.ComponentType<{ size?: number }>, { size: 12 })}
                          </span>
                        )}
                      </h3>
                      {podcast.description && (
                        <p className="podcast-description">{podcast.description}</p>
                      )}
                      <div className="podcast-meta">
                        <span className="podcast-views">
                          {React.createElement(FiEye as React.ComponentType<{ size?: number }>, { size: 14 })}
                          {podcast.views_count.toLocaleString()}
                        </span>
                        {podcast.duration && (
                          <span className="podcast-duration-meta">
                            {React.createElement(FiClock as React.ComponentType<{ size?: number }>, { size: 14 })}
                            {formatDuration(podcast.duration)}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
                })}
              </motion.div>
              <MaterialPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setPage}
                disabled={loading}
              />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodcastPage;

